import { useEffect, useRef, useState } from "react";

import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { useHasClassStarted } from "hooks/use_has_class_started";
import { useUserProperties } from "hooks/use_user_properties";
import { addBreakoutTalkTime, addClassroomTalkTime } from "shared/reducers/classroom";
import { ClassEventTypeType } from "shared/types/class_event";
import { classroomSelectors, localSelectors } from "store";
import { createClassEvent } from "store/reducers/monitoring";

const MIN_VOICE_EVENT_DURATION_FOR_TALK_TIME = 2000;

// Other class events are created directly in components
function ClassEventObserver() {
  const dispatch = useAppDispatch();
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const currentUser = useUserProperties(currentUserId, [
    "is_hand_raised",
    "is_speaking",
    "reaction",
    "vote",
  ]);
  const classInfo = useAppSelector(classroomSelectors.selectClassInfo());
  const hasClassStarted = useHasClassStarted(classInfo);
  const activeBreakoutSessionId = useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );
  const breakoutRoomId = useAppSelector(
    classroomSelectors.selectActiveBreakoutRoomForCurrentUser(),
  )?.id;
  const isInControlRoom = !!activeBreakoutSessionId && !breakoutRoomId;
  const activeRoomId = useAppSelector(classroomSelectors.selectActiveRoomIdForCurrentUser());
  const previousIsHandRaised = useRef(currentUser?.is_hand_raised);
  const previousReaction = useRef(currentUser?.reaction);
  const previousVote = useRef(currentUser?.vote);
  const [voiceEventStart, setVoiceEventStart] = useState<number | null>(null);

  useEffect(() => {
    if (currentUser?.is_hand_raised && !previousIsHandRaised.current) {
      dispatch(
        createClassEvent({
          type: ClassEventTypeType.EMOTION,
          userId: currentUserId,
          roomId: activeRoomId,
          data: { emotion: "hand-raise" },
        }),
      );
    }
    previousIsHandRaised.current = currentUser?.is_hand_raised;
  }, [activeRoomId, currentUser?.is_hand_raised, currentUserId, dispatch, previousIsHandRaised]);

  useEffect(() => {
    if (currentUser?.reaction && currentUser.reaction !== previousReaction.current) {
      dispatch(
        createClassEvent({
          type: ClassEventTypeType.EMOTION,
          userId: currentUserId,
          roomId: activeRoomId,
          data: { emotion: currentUser.reaction },
        }),
      );
    }
    previousReaction.current = currentUser?.reaction;
  }, [activeRoomId, currentUser?.reaction, currentUserId, dispatch, previousReaction]);

  useEffect(() => {
    if (currentUser?.vote && currentUser.vote !== previousVote.current) {
      dispatch(
        createClassEvent({
          type: ClassEventTypeType.QUICK_POLL_RESPONSE,
          userId: currentUserId,
          roomId: activeRoomId,
          data: { response: currentUser.vote },
        }),
      );
    }
    previousVote.current = currentUser?.vote;
  }, [activeRoomId, currentUser?.vote, currentUserId, dispatch, previousVote]);

  useEffect(() => {
    if (currentUser?.is_speaking) {
      if (!isInControlRoom && !voiceEventStart) {
        window.flightLogger.log("[ClassEventObserver] Start of voice event");
        setVoiceEventStart(Date.now());
      }
    } else if (currentUser?.is_speaking === false && voiceEventStart) {
      const duration = Date.now() - voiceEventStart;
      window.flightLogger.log(`[ClassEventObserver] End of voice event (duration: ${duration})`);
      dispatch(
        createClassEvent({
          type: ClassEventTypeType.VOICE,
          userId: currentUserId,
          roomId: activeRoomId,
          data: { duration },
        }),
      );
      setVoiceEventStart(null);

      // Add talk time if the voice event was long enough and class has started
      if (hasClassStarted && duration >= MIN_VOICE_EVENT_DURATION_FOR_TALK_TIME) {
        if (breakoutRoomId) {
          dispatch(
            addBreakoutTalkTime({
              userId: currentUserId,
              breakoutRoomId,
              duration,
            }),
          );
        } else {
          dispatch(addClassroomTalkTime({ userId: currentUserId, duration }));
        }
      }
    }
  }, [
    activeRoomId,
    breakoutRoomId,
    currentUser?.is_speaking,
    currentUserId,
    dispatch,
    hasClassStarted,
    isInControlRoom,
    voiceEventStart,
  ]);

  return null;
}

export { ClassEventObserver };
